import React, { useCallback, useEffect, useState } from 'react'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { Box, Button, RequestError } from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { Approval } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useApprovalsPreference } from 'hooks/useApprovalsPreference'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePendingApprovalDetails } from 'hooks/usePendingApprovalDetails'
import {
  ApprovalDecisionStateType,
  ApprovalStatuses,
  HandleApprovalDecisionModalCallback,
} from 'types/approvals'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { ApprovalContextProvider } from '../../contexts/approvalsContext'
import ApprovalDecisionModal from './components/ApprovalDecisionModal'
import ApprovalHeaderButtons from './components/ApprovalHeaderButtons'
import ApprovalPreferenceSettingsModalV2 from './components/ApprovalPreferenceSettingsModalV2'
import ApprovalPreferenceSuccessModal from './components/ApprovalPreferenceSuccessModal'
import ApprovalTabs from './components/ApprovalTabs'
import { useEnabledApprovalFlows } from './components/hooks/useApprovalsFlowSettings'
import { useCheckUnapprovableTransactions } from './components/hooks/useCheckUnapprovableTransactions'
import ManageApproversModal from './components/ManageApproversModal'

const Approvals = () => {
  const { t } = useTranslation()
  const { tab } = queryString.parse(globalThis.location.search) as { tab?: 'APPROVED' | 'REJECTED' }

  const { currentAccount } = useAccountsContext()
  const {
    profile: { emailAddress: currentEmailAddress },
  } = useProfileContext()
  const permissions = useMappedRoles()?.manageTeamMemberApprovers
  const approvalPermissions = useMappedRoles()?.manageApprovals
  const canEdit = permissions?.canEdit
  const canManageApproval = approvalPermissions?.canEdit

  const currentAccountReference = currentAccount?.reference
  const [showApprovalPreferenceModal, setShowApprovalPreferenceModal] = useState(false)
  const [showApprovalPreferenceSuccessModal, setShowApprovalPreferenceSuccessModal] =
    useState(false)
  const [showManageApproversModal, setShowManageApproversModal] = useState(false)
  const [approvalDecision, setApprovalDecision] = useState<ApprovalDecisionStateType>({
    decision: undefined,
    showApprovalDecisionModal: false,
    isCompleted: false,
  })
  const [checkedRowData, setCheckedRowData] = useState<unknown[]>([])
  const [activeTab, setActiveTab] = useState<keyof typeof ApprovalStatuses>(
    tab || ApprovalStatuses.PENDING
  )

  const {
    approvalFlowPreferences = {
      enabledFlows: [],
      approvalBands: [],
    },
    isApprovalPreferenceError,
    isApprovalPreferenceSuccess,
    isFetchingApprovalPreference,
  } = useApprovalsPreference(currentAccountReference, !!currentAccountReference)

  const enabledApprovalFlowsTranslation = useEnabledApprovalFlows(
    approvalFlowPreferences.enabledFlows
  )

  const {
    pendingApprovals,
    numberOfPendingApprovals,
    hasPendingApprovals,
    isPendingApprovalsError,
    isPendingApprovalsSuccess,
    isFetchingPendingApprovals,
    fetchNextPendingPage,
  } = usePendingApprovalDetails(currentAccountReference, !!currentAccountReference)

  const isError = isApprovalPreferenceError || isPendingApprovalsError
  const isSuccess = isApprovalPreferenceSuccess && isPendingApprovalsSuccess
  const isFetching = isFetchingApprovalPreference || isFetchingPendingApprovals

  const unapprovableTransactions = useCheckUnapprovableTransactions(
    checkedRowData,
    currentEmailAddress
  )

  useEffect(() => {
    setApprovalDecision({ ...approvalDecision, isCompleted: false })
    track.Amp.track(AmplitudeEvent.APPROVALS_HOME, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      selectedItems: checkedRowData?.length,
    })
  }, [])

  const handleApprovalPreferenceSettingsModal = useCallback(
    ({ isVisible }: { isVisible?: boolean }) => {
      setShowApprovalPreferenceModal(!!isVisible)
    },
    [showApprovalPreferenceModal]
  )

  const handleApprovalPreferenceSuccessModal = useCallback(
    ({ isVisible }: { isVisible?: boolean }) => {
      setShowApprovalPreferenceSuccessModal(!!isVisible)
    },
    [showApprovalPreferenceSuccessModal]
  )

  const handleManageApproversModal = useCallback(
    ({ isVisible }: { isVisible?: boolean }) => {
      setShowManageApproversModal(!!isVisible)
      isVisible &&
        track.Amp.track(AmplitudeEvent.MANAGE_APPROVERS_INITIATE, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.VIEW,
        })
    },
    [showManageApproversModal]
  )

  const handleApprovalDecisionModal = useCallback(
    ({ isVisible, decision, isCompleted = false }: HandleApprovalDecisionModalCallback) => {
      setApprovalDecision({
        showApprovalDecisionModal: !!isVisible,
        decision,
        isCompleted,
      })
      const titleOfEventInput =
        decision === Approval.DECISION.APPROVE
          ? AmplitudeEvent?.APPROVE_TRANSACTION_INITIATE
          : AmplitudeEvent?.REJECT_TRANSACTION_INITIATE
      decision &&
        track.Amp.track(titleOfEventInput, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.VIEW,
          selectedItems: checkedRowData?.length,
        })
    },
    [approvalDecision]
  )
  return (
    <ApprovalContextProvider
      permissions={{ canEdit, canManageApproval }}
      pendingApprovalDetails={{
        pendingApprovals,
        numberOfPendingApprovals,
        hasPendingApprovals,
        fetchNextPendingPage,
      }}
      approvalsConfig={{
        enabledFlows: approvalFlowPreferences?.enabledFlows,
        approvalBands: approvalFlowPreferences?.approvalBands,
        allApprovalFlowsAreDisabled: !enabledApprovalFlowsTranslation,
        approvalDecisionIsCompleted: approvalDecision?.isCompleted,
        handleTableRowCheck: value => setCheckedRowData(value),
        handleManageApproversModal,
        handleApprovalDecisionModal,
        handleApprovalPreferenceSettingsModal,
        handleApprovalPreferenceSuccessModal,
      }}
      isFetching={isFetching}
      isSuccess={isSuccess}
      isError={isError}
    >
      <PageHeader
        description={
          hasPendingApprovals || enabledApprovalFlowsTranslation ? (
            <Box className="text-foreground-subdued text-body-regular">
              {enabledApprovalFlowsTranslation
                ? `${t('approvals.enabledOutgoingPayments')} ${enabledApprovalFlowsTranslation}.`
                : t('approvals.disabledApprovalsHeadline')}{' '}
              <Button
                variant="ghost"
                noPadding
                onClick={() => setShowApprovalPreferenceModal(true)}
              >
                {canEdit
                  ? t('approvals.editApprovalPreference')
                  : t('approvals.viewApprovalPreference')}
              </Button>
            </Box>
          ) : (
            t('approvals.defaultPageHeaderDescription')
          )
        }
        renderRightSection={
          hasPendingApprovals && (
            <ApprovalHeaderButtons
              activeTab={activeTab}
              hasCheckedPendingTransactions={!!checkedRowData?.length}
              cannotApprove={unapprovableTransactions}
            />
          )
        }
      />

      {isApprovalPreferenceError && !isFetchingApprovalPreference && <RequestError />}

      {!isApprovalPreferenceError && (
        <ApprovalTabs queryTab={tab} activeTab={activeTab} setActiveTab={setActiveTab} />
      )}

      {showManageApproversModal && <ManageApproversModal />}
      {approvalDecision.showApprovalDecisionModal && approvalDecision.decision && (
        <ApprovalDecisionModal
          decision={approvalDecision.decision}
          selectedItems={checkedRowData}
        />
      )}

      {showApprovalPreferenceModal && (
        <>
          {showApprovalPreferenceSuccessModal ? (
            <ApprovalPreferenceSuccessModal />
          ) : (
            <ApprovalPreferenceSettingsModalV2 />
          )}
        </>
      )}
    </ApprovalContextProvider>
  )
}

export default Approvals
